/**
 * KpHeatmapTable — students (rows) × knowledge points (columns), grouped
 * by axis. Evidence gating per spec E4: a cell below the evidence
 * threshold renders 证据不足 (hatched neutral), never a confident color.
 */
import type { KpHeatmap } from "../api/school";

const AXIS_LABEL: Record<string, string> = {
  listening: "听力",
  speaking: "口语",
  reading: "阅读",
  writing: "写作",
  vocabulary: "词汇",
  grammar: "语法",
};

function cellColor(score: number): string {
  if (score >= 85) return "bg-emerald-100 text-emerald-900";
  if (score >= 70) return "bg-sky-100 text-sky-900";
  if (score >= 60) return "bg-amber-100 text-amber-900";
  return "bg-rose-100 text-rose-900";
}

const TREND_MARK: Record<string, string> = { improving: "↑", declining: "↓" };

export function KpHeatmapTable({ data }: { data: KpHeatmap }) {
  if (data.kps.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-400">
        暂无知识点掌握度数据 — 完成一次录分归因后生成
      </p>
    );
  }

  // Group columns by axis for the two-row header.
  const axisGroups: { axis: string; label: string; count: number }[] = [];
  for (const kp of data.kps) {
    const label = kp.axis_zh || AXIS_LABEL[kp.axis ?? ""] || kp.axis || "其他";
    const last = axisGroups[axisGroups.length - 1];
    if (last && last.axis === (kp.axis ?? "")) last.count += 1;
    else axisGroups.push({ axis: kp.axis ?? "", label, count: 1 });
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <table className="text-sm border-collapse">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th rowSpan={2} className="text-left px-3 py-2 font-medium w-32 sticky left-0 bg-slate-50 z-10">
              学生
            </th>
            {axisGroups.map((g, i) => (
              <th key={i} colSpan={g.count} className="px-2 py-1 font-medium border-l border-slate-200 text-center">
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            {data.kps.map((kp) => (
              <th
                key={kp.kp_id}
                title={kp.name_zh}
                className="px-2 py-1 font-normal text-xs text-slate-500 border-l border-slate-100 text-center max-w-[7rem]"
              >
                <span className="block truncate">{kp.name_zh}</span>
                <span className="text-slate-300">{kp.code}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.students.map((s) => (
            <tr key={s.student_id} className="border-t border-slate-100">
              <td className="px-3 py-2 text-slate-800 whitespace-nowrap sticky left-0 bg-white z-10">
                {s.name_zh}
                {s.name_en && <span className="text-slate-400 text-xs ml-1">({s.name_en})</span>}
              </td>
              {data.kps.map((kp) => {
                const c = data.cells[s.student_id]?.[kp.kp_id];
                if (!c || c.mastery === null)
                  return (
                    <td key={kp.kp_id} className="px-1 py-1 text-center text-xs text-slate-300">
                      —
                    </td>
                  );
                if (c.low_evidence)
                  return (
                    <td key={kp.kp_id} className="px-1 py-1 text-center">
                      <span
                        title={`证据不足(${c.evidence_count}/${data.evidence_threshold} 次测量)`}
                        className="inline-block px-1.5 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-400 border border-dashed border-slate-300"
                      >
                        {Math.round(c.mastery)}?
                      </span>
                    </td>
                  );
                return (
                  <td key={kp.kp_id} className="px-1 py-1 text-center">
                    <span
                      title={`证据 ${c.evidence_count} 次`}
                      className={`inline-block px-1.5 py-0.5 rounded text-xs font-mono ${cellColor(c.mastery)}`}
                    >
                      {Math.round(c.mastery)}
                      {c.trend && TREND_MARK[c.trend] ? TREND_MARK[c.trend] : ""}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-3 py-2 text-xs text-slate-400 border-t border-slate-100">
        虚线格 = 证据不足(少于 {data.evidence_threshold} 次测量),数值仅供参考;↑/↓ = 相比上次趋势
      </p>
    </div>
  );
}
